import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { NEEDS, NOT_CONNECTED, READY, UNAVAILABLE, notConnected } from '@/lib/ops-matrix.js'
import { formatReceived } from '@/components/engine/unmatched/model.js'
import CampaignCarousel, { ALL, stopsOf } from './CampaignCarousel.jsx'
import { AllChart, SingleChart, toneOf } from './SendsCharts.jsx'
import { funnelHeights, percent } from './chart-model.js'
import { HOME_CSS } from './home-style.js'

export const COPY = {
  notConnected: 'Not connected yet.',
  unavailable: 'Unavailable',
  tableNotConnected: 'Business campaigns are not connected yet.',
  tableUnavailable: 'Business campaigns could not be loaded.',
  tableEmpty: 'No business campaigns yet.',
  loading: 'Loading the ops matrix',
  heldNote: 'held or unmatched, not total replies',
  candidates: 'Candidate campaigns have no list on Engine yet. Melissa job outreach is sent by her from her own mail.',
  placement: 'Squares show the mailbox connection state, not inbox placement. Placement checks are not connected.',
}

const TONE_OK = new Set(['success', 'warning', 'error', 'neutral'])

function Dot({ tone }) {
  return <span aria-hidden="true" className="eh-dot" data-tone={TONE_OK.has(tone) ? tone : 'none'} />
}

const Nc = ({ needs }) => <span data-state="notConnected" title={needs || COPY.notConnected} className="eh-note">n/c</span>
const Bad = () => <span data-state="unavailable" role="status" className="eh-bad">{COPY.unavailable}</span>

// A cell: a number or text only from a ready value.
function Value({ cell, format = String, className = '' }) {
  if (cell.kind === READY) return <span data-state="ready" className={`eh-mono ${className}`}>{format(cell.value)}</span>
  if (cell.kind === NOT_CONNECTED) return <Nc needs={cell.needs} />
  return <Bad />
}

function Panel({ children, label, className = '' }) {
  return <section aria-label={label} className={`eh-panel ${className}`}>{children}</section>
}

// What waits on Apollo. Each queue has its own screen; until that screen is connected the row says so
// and shows no count, so an empty tile is never read as nothing waiting.
export const WAITING_ROWS = [
  { key: 'pulls', label: 'Lead pulls', to: '/approvals/pulls', note: 'Shows proposed lead batches.' },
  { key: 'drafts', label: 'Drafts', to: '/approvals/drafts', note: 'Shows drafts awaiting review.' },
  { key: 'batches', label: 'Batch release', to: '/approvals/release', note: 'Shows approved batches to release.' },
]

function Waiting({ model }) {
  const held = model.headline.replies
  return (
    <section aria-label="Waiting on you" data-slot="waiting">
      <h2 className="sr-only">Waiting on you</h2>
      <ul className="eh-chips">
        <li data-slot="waiting-held">
          <Link to="/replies/unmatched" className="eh-chip eh-chip-held">
            <Value cell={held} />
            <span>{held.kind === READY ? `held ${held.value === 1 ? 'reply' : 'replies'} to review` : 'Held replies'}</span>
            {held.kind === READY && <span className="sr-only">({COPY.heldNote})</span>}
          </Link>
        </li>
        {WAITING_ROWS.map(row => (
          <li key={row.key} data-slot="waiting-row" data-source={row.key}>
            <Link to={row.to} title={row.note} className="eh-chip eh-chip-nc">
              <span>{row.label}</span>
              <span><span data-state="notConnected">{COPY.notConnected}</span></span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function MailboxCell({ cell }) {
  if (cell.kind === NOT_CONNECTED) return <span className="inline-flex items-center gap-2"><Dot /><span className="eh-note">none yet</span></span>
  if (cell.kind === UNAVAILABLE) return <Bad />
  return (
    <span className="inline-flex items-start gap-2 min-w-0">
      <span className="pt-1.5"><Dot tone={cell.tone} /></span>
      <span className="min-w-0"><span className="block break-words [overflow-wrap:anywhere]">{cell.address}</span><span className="eh-note block">{cell.word}</span></span>
    </span>
  )
}

function Kpi({ label, cell, note, format }) {
  return (
    <div data-slot="kpi" className="eh-kpi">
      <span className="eh-label">{label}</span>
      <span className="eh-kpi-value"><Value cell={cell} format={format} /></span>
      <span className="eh-note">{note}</span>
    </div>
  )
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`
// A tenant-wide answer under a selected workspace counts every workspace, so each ready note says so.
const scopeWords = (cell, allWorkspaces) => (allWorkspaces && cell.kind === READY ? ', all workspaces' : '')
// Sent is the confirmed sends over the series range. Days with unconfirmed sends add nothing and are counted in the note.
const sentTile = (cell, allWorkspaces = false) => ({
  label: 'Sent', cell, format: v => v.sent,
  note: (cell.kind !== READY ? 'sends and replies route' : !cell.value.recorded ? `none recorded, last ${cell.value.span} days` : `confirmed, last ${cell.value.span} days${cell.value.unprovenDays ? `, ${plural(cell.value.unprovenDays, 'unconfirmed day')} left out` : ''}${cell.value.gapDays ? `, ${plural(cell.value.gapDays, 'day')} with no count` : ''}`) + scopeWords(cell, allWorkspaces),
})
// Matched replies over those confirmed sends. It never reads as a precise rate when a day is not proven without saying so.
const rateTile = (cell, allWorkspaces = false) => ({
  label: 'Reply rate', cell, format: v => (v.rate === null ? 'n/a' : percent(v.rate)),
  note: (cell.kind !== READY ? 'sends and replies route' : cell.value.rate === null ? (cell.value.gapDays ? `${plural(cell.value.gapDays, 'day')} with no count` : cell.value.recorded ? 'no confirmed sends' : 'no sends recorded') : cell.value.unprovenDays ? `confirmed sends only, ${plural(cell.value.unprovenDays, 'unconfirmed day')} left out` : 'matched replies over confirmed sends') + scopeWords(cell, allWorkspaces),
})

function kpisOf(model, selected, allWorkspaces = false) {
  if (selected === ALL) {
    const a = model.all
    const c = model.headline.campaigns
    return [
      { label: 'Recipients', cell: a.recipients, note: 'across the business list' },
      { label: 'Drafts waiting', cell: a.drafts, note: 'not yet reviewed' },
      { label: 'Approved', cell: a.approved, note: 'current draft accepted' },
      { label: 'In send queue', cell: a.queued, note: 'prepared, paused, sending off' },
      sentTile(a.sent, allWorkspaces),
      rateTile(a.rate, allWorkspaces),
      { label: 'Held replies', cell: a.held, note: COPY.heldNote },
      { label: 'Active campaigns', cell: c, format: v => <>{v.active} <span className="eh-note">of {v.total}</span></>, note: c.kind === READY ? `${c.value.paused} paused` : 'business campaign list' },
    ]
  }
  const r = model.campaigns.find(x => x.id === selected)
  return [
    { label: 'Recipients', cell: r.leads, note: 'on the campaign' },
    { label: 'Drafts waiting', cell: r.waiting, note: 'not yet reviewed' },
    { label: 'Approved', cell: r.approved, note: 'current draft accepted' },
    { label: 'In send queue', cell: r.queued, note: 'prepared, paused, sending off' },
    sentTile(r.sent, allWorkspaces),
    rateTile(r.rate, allWorkspaces),
    { label: 'Held replies', cell: r.replies, note: COPY.heldNote },
  ]
}

function stagesOf(model, selected) {
  const src = selected === ALL
    ? { leads: model.all.recipients, drafts: model.all.drafted, approved: model.all.approved, queued: model.all.queued }
    : model.campaigns.find(x => x.id === selected)
  const lack = notConnected(NEEDS.funnelSent)
  return [
    { label: 'Recipients', cell: src.leads }, { label: 'Drafted', cell: src.drafts }, { label: 'Approved', cell: src.approved },
    { label: 'Queued', cell: src.queued }, { label: 'Sent', cell: lack }, { label: 'Replied', cell: lack },
  ]
}

const FUNNEL_HINT = 'Hover a column for the step rate'

// The step rate is said only between two ready stages, so a stage that is not connected never makes a percentage.
function funnelNote(stages, on) {
  if (on === null) return FUNNEL_HINT
  const here = stages[on].cell
  if (on === 0) return here.kind === READY ? `${here.value} ${stages[0].label.toLowerCase()}` : FUNNEL_HINT
  const prev = stages[on - 1].cell
  if (here.kind !== READY || prev.kind !== READY) {
    const failed = here.kind === UNAVAILABLE || prev.kind === UNAVAILABLE
    return `${stages[on - 1].label} to ${stages[on].label.toLowerCase()}: ${failed ? 'unavailable' : 'not connected'}`
  }
  return `${stages[on - 1].label} to ${stages[on].label.toLowerCase()}: ${prev.value ? ((here.value / prev.value) * 100).toFixed(1) : '0'}%`
}

function Funnel({ stages, on, onEnter }) {
  const heights = funnelHeights(stages.map(s => (s.cell.kind === READY ? s.cell.value : NaN)), 152)
  return (
    <div data-slot="funnel" className="eh-col">
      <div className="flex items-baseline justify-between gap-2"><h3 className="eh-chart-title">Funnel</h3><span data-slot="funnel-note" className="eh-note">{funnelNote(stages, on)}</span></div>
      <ol className="eh-funnel-cols" onMouseLeave={() => onEnter(null)}>
        {stages.map((s, i) => (
          <li key={s.label} data-slot="funnel-stage" className="eh-stage-col" onMouseEnter={() => onEnter(i)}>
            <span className="eh-fv"><Value cell={s.cell} /></span>
            {heights[i] === null
              ? <div aria-hidden="true" className="eh-fcol eh-fcol-nc" />
              : <div aria-hidden="true" data-on={on === i ? 'true' : 'false'} style={{ height: `${Math.max(8, heights[i] + 8)}px` }} className="eh-fcol" />}
            <span className="eh-flabel">{s.label}</span>
          </li>
        ))}
      </ol>
      <span className="eh-note">Column heights use a square root scale so small stages stay visible.</span>
    </div>
  )
}

const WORD = { success: 'connection passed', warning: 'needs attention', neutral: 'not checked yet' }

function Inboxes({ model, cellInfo, onCell }) {
  const cell = model.inboxes
  const rows = cell.kind === READY ? cell.value : []
  const groups = rows.reduce((m, r) => m.set(r.provider, [...(m.get(r.provider) || []), r]), new Map())
  return (
    <Panel label="Inboxes" className="eh-inboxes lg:col-span-2" >
      <div className="flex flex-wrap items-baseline justify-between gap-2.5">
        <h2 className="eh-h3">Inboxes</h2>
        {rows.length > 0 && <span data-slot="inbox-info" className="eh-note" style={{ minHeight: 18 }}>{cellInfo || 'Hover an inbox to see its address and connection state'}</span>}
      </div>
      {cell.kind !== READY ? <p className="text-sm"><Value cell={cell} /> <span className="eh-note">{cell.kind === NOT_CONNECTED ? 'Mailboxes are not connected yet.' : 'Mailboxes could not be loaded.'}</span></p>
        : rows.length === 0 ? <p data-slot="inbox-empty" className="eh-note">No mailboxes yet.</p>
        : (
          <div data-slot="inbox-grid" className="eh-inbox-groups">
            {[...groups].map(([provider, list]) => (
              <ul key={provider} aria-label={provider} className="eh-inbox-group">
                <li className="eh-group-name">{provider}</li>
                {list.map(r => {
                  const info = `${r.address}, ${r.word}`
                  return (
                    <li key={r.mailboxId} data-slot="inbox" className="eh-inbox">
                      <span className="eh-inbox-name" title={r.address}>{r.address}</span>
                      <span className="sr-only">{r.word}</span>
                      <button type="button" className="eh-cell" data-tone={r.tone} aria-label={info} onMouseEnter={() => onCell(info)} onFocus={() => onCell(info)} onMouseLeave={() => onCell(null)} onBlur={() => onCell(null)} />
                    </li>
                  )
                })}
              </ul>
            ))}
          </div>
        )}
      <p data-state="notConnected" className="eh-note">{COPY.placement}</p>
    </Panel>
  )
}

const toneOfCell = (cell, okTone = 'success') => (cell.kind === READY ? okTone : cell.kind === UNAVAILABLE ? 'error' : undefined)

function MachineRow({ label, cell, tone, format, note }) {
  return (
    <div data-slot="machine-row" className="eh-mrow">
      <Dot tone={tone} />
      <span className="min-w-0 flex-1"><span className="eh-mrow-name">{label}</span><span className="eh-mrow-note">{note}</span></span>
      <span className="eh-mrow-value"><Value cell={cell} format={format} /></span>
    </div>
  )
}

function Machine({ model }) {
  const m = model.machine
  const sys = model.headline.system
  return (
    <Panel label="The machine" className="eh-card">
      <h2 data-slot="machine-title" className="eh-h3">The machine</h2>
      <div data-slot="machine">
        <MachineRow label="Reply webhook" note="last authenticated receipt" cell={m.webhook} tone={toneOfCell(m.webhook)} format={v => (v.at === null ? 'None yet' : formatReceived(v.at))} />
        <MachineRow label="Mailboxes" note="connection and policy" cell={sys} tone={sys.kind === READY ? sys.value.tone : toneOfCell(sys)} format={v => v.text} />
        <MachineRow label="Send queue" note="prepared jobs waiting, paused, sending off" cell={m.queue} tone={toneOfCell(m.queue)} />
        <MachineRow label="Engine API" note="HTTP errors, last 24 hours" cell={m.apiErrors} tone={toneOfCell(m.apiErrors)} />
        <MachineRow label="Deliverability" note="placement checks and unsubscribe land next" cell={m.deliverability} tone={toneOfCell(m.deliverability)} />
      </div>
    </Panel>
  )
}

function Cost({ model }) {
  return (
    <Panel label="Cost per reply" className="eh-card">
      <div data-slot="cost" className="eh-cost-head">
        <h2 className="eh-h3" style={{ margin: 0 }}>Cost per reply</h2>
        <Value cell={model.cost} className="text-lg" />
      </div>
      <div aria-hidden="true" className="eh-cost-bar" />
      <p className="eh-note">{model.cost.needs}</p>
    </Panel>
  )
}

const FRESH = { sel: ALL, hidden: [], hover: { key: null, idx: null }, funnel: null, cell: null }

function Loading() {
  return (
    <div className="eh" data-slot="home-loading">
      <style>{HOME_CSS}</style>
      <Panel label={COPY.loading} className="eh-loading">
        <p role="status" className="eh-note">{COPY.loading}</p>
        <div aria-hidden="true" className="eh-skel eh-skel-wide" />
        <div aria-hidden="true" className="eh-skel" />
      </Panel>
    </div>
  )
}

// The selection, the hidden legend entries and the hover belong to one workspace. A different workspace reads
// as a fresh view, so nothing chosen before the switch can colour the next one.
export default function OpsMatrixView({ state, model, scope = null, spendVisible = false }) {
  const [ui, setUi] = useState({ scope, ...FRESH })
  const groupRef = useRef(null)
  const mine = ui.scope === scope ? ui : FRESH
  const update = patch => setUi({ ...mine, ...patch, scope })
  const { sel, hidden, hover } = mine
  // Leaving a workspace drops what was chosen in it for good, so coming back to it does not bring it back.
  useEffect(() => { setUi(u => (u.scope === scope ? u : { scope, ...FRESH })) }, [scope])

  if (state === 'loading' || !model) return <Loading />

  const stops = stopsOf(model)
  const selected = stops.some(s => s.id === sel) ? sel : ALL
  const idx = hover.key === selected ? hover.idx : null
  const campaigns = model.campaigns.filter(c => c.kind === 'business')
  const pick = (id, focus) => {
    update({ sel: id, hover: FRESH.hover, funnel: null })
    if (focus) setTimeout(() => groupRef.current?.querySelector?.('[aria-checked="true"]')?.focus?.(), 0)
  }
  const toggle = id => update({ hidden: hidden.includes(id) ? hidden.filter(x => x !== id) : [...hidden, id], hover: FRESH.hover })
  const onHover = i => update({ hover: { key: selected, idx: i } })
  const message = model.listState === NOT_CONNECTED ? COPY.tableNotConnected : model.listState === UNAVAILABLE ? COPY.tableUnavailable : campaigns.length === 0 ? COPY.tableEmpty : null
  const current = stops.find(s => s.id === selected)
  const row = selected === ALL ? null : campaigns.find(c => c.id === selected)
  const tone = toneOf(Math.max(0, campaigns.findIndex(c => c.id === selected)))
  const stages = stagesOf(model, selected)
  // The legacy read answers for the whole tenant: a workspace is selected but the answer names none.
  const allWorkspaces = scope != null && model.series.kind === READY && model.series.value.workspaceId === null

  return (
    <div data-slot="ops-matrix" className="eh">
      <style>{HOME_CSS}</style>
      <div className="eh-head">
        <div className="eh-head-left">
          {model.series.kind !== READY && <span data-slot="chart-pill" className="eh-pill">{model.series.kind === NOT_CONNECTED ? 'Sends and replies per day are not connected yet' : 'Sends and replies per day could not be loaded'}</span>}
        </div>
        <Waiting model={model} />
      </div>
      <CampaignCarousel stops={stops} selected={selected} onPick={pick} groupRef={groupRef} />
      {message && <p data-slot="matrix-message" className={model.listState === UNAVAILABLE ? 'eh-bad' : 'eh-note'}>{message}</p>}
      <p data-slot="candidate-note" className="eh-note">{COPY.candidates}</p>

      <Panel label={`${current.name} detail`} className="eh-detail">
        <div data-slot="selected" data-selected={selected} className="eh-detail-head">
          <h2 className="eh-h2">{current.name}</h2>
          {row ? <div className="min-w-0 text-sm"><MailboxCell cell={row.mailbox} /></div> : <span className="eh-note">{current.sub}</span>}
        </div>
        <div data-slot="kpis" className="eh-kpis">
          {kpisOf(model, selected, allWorkspaces).map(k => <Kpi key={k.label} {...k} />)}
        </div>
        <div className="eh-two">
          <div className="eh-col">
            <h3 className="eh-chart-title">{selected === ALL ? 'Sent per day, every campaign' : 'Sends and replies'}</h3>
            {selected === ALL
              ? <AllChart series={model.series} campaigns={campaigns} hidden={hidden} onToggle={toggle} hover={idx} onHover={onHover} allWorkspaces={allWorkspaces} />
              : <SingleChart series={model.series} campaign={selected} name={current.name} tone={tone} hover={idx} onHover={onHover} allWorkspaces={allWorkspaces} />}
          </div>
          <Funnel stages={stages} on={mine.funnel} onEnter={i => update({ funnel: i })} />
        </div>
      </Panel>

      <div className="eh-lower">
        <Inboxes model={model} cellInfo={mine.cell} onCell={info => update({ cell: info })} />
        <div className="eh-side">
          <Machine model={model} />
          {spendVisible && <Cost model={model} />}
        </div>
      </div>
    </div>
  )
}
